<template>
  <div class="pb-page ib">
    <PageHeader title="Inbox" :subtitle="subtitle">
      <router-link to="/testimonials/import" class="pb-button outline"><i class="mdi mdi-import pb-ico" aria-hidden="true"></i>Import</router-link>
      <!-- Outline while the inbox is empty, so the empty state's "Copy link" is the one filled button, and until the
           counts first arrive, so it never flips from solid to outline. Not on "All brands": each brand has its own link. -->
      <button v-if="formLink && brandFilter !== 'all'" type="button" :class="['pb-button', { outline: !countsKnown || !totalAll }]" @click="copyLink"><i class="mdi mdi-content-copy pb-ico" aria-hidden="true"></i>Copy collection link</button>
    </PageHeader>

    <div class="ib-tabs" role="tablist" aria-label="Status">
      <button v-for="tab in tabs" :key="tab.key" type="button" role="tab" :aria-selected="status === tab.key ? 'true' : 'false'"
              :class="['ib-tab', { active: status === tab.key }]" @click="setStatus(tab.key)">
        {{ tab.label }}<span class="ib-count">{{ tabCount(tab.key) }}</span>
      </button>
    </div>

    <form class="ib-filters" role="search" @submit.prevent="submitSearch">
      <div class="ib-search">
        <label class="pb-sr-only" for="ib-q">Search feedback</label>
        <i class="mdi mdi-magnify ib-search-icon" aria-hidden="true"></i>
        <input id="ib-q" v-model="q" class="pb-input" type="search" placeholder="Search names and answers" maxlength="120">
      </div>
      <button type="button" class="pb-button outline ib-more" :aria-expanded="showFilters ? 'true' : 'false'" aria-controls="ib-filter-panel" @click="showFilters = !showFilters">
        <i class="mdi mdi-tune-variant pb-ico" aria-hidden="true"></i>Filters<span v-if="activeFilters" class="ib-badge">{{ activeFilters }}</span></button>
      <button type="submit" class="pb-button outline">Search</button>
    </form>
    <div v-show="showFilters" id="ib-filter-panel" class="pb-card flat ib-panel">
      <div v-if="brandFilter !== 'all'" class="ib-f"><label class="pb-label" for="ib-form">Collection form</label>
        <select id="ib-form" v-model="formId" class="pb-select" @change="search"><option value="">Any form</option>
          <option v-for="form in forms" :key="form.id" :value="form.id">{{ form.name }}</option></select></div>
      <div class="ib-f"><label class="pb-label" for="ib-rating">Rating</label>
        <select id="ib-rating" v-model="rating" class="pb-select" @change="search"><option value="">Any rating</option>
          <option v-for="n in [5, 4, 3, 2, 1]" :key="n" :value="n">{{ n }} star{{ n === 1 ? '' : 's' }}</option></select></div>
      <div class="ib-f"><label class="pb-label" for="ib-from">From</label><input id="ib-from" v-model="from" class="pb-input" type="date" @change="search"></div>
      <div class="ib-f"><label class="pb-label" for="ib-to">To</label><input id="ib-to" v-model="to" class="pb-input" type="date" @change="search"></div>
      <div class="ib-f"><label class="pb-label" for="ib-tag">Tag</label><input id="ib-tag" v-model="tag" class="pb-input" maxlength="30" placeholder="e.g. launch" @change="search"></div>
      <div class="ib-f ib-clear"><button type="button" class="pb-button ghost" :disabled="!activeFilters && !q" @click="clearFilters">Clear filters</button></div>
    </div>

    <p v-if="matching && !loading && !failed && totalAll" class="pb-muted ib-matching" role="status">
      <i class="mdi mdi-magnify" aria-hidden="true"></i>{{ matchingText }}</p>

    <div v-if="selected.length" class="ib-bulk" role="region" aria-label="Selected feedback">
      <span><strong>{{ selected.length }}</strong> selected</span>
      <div class="pb-actions">
        <template v-if="status === 'approved' || status === 'in_review' || status === 'new'">
          <LockedAction v-if="publishCap" feature="published" look="pb-button sm" label="Publish" :cap="publishCap" :used="usage.published || 0" />
          <button v-else type="button" class="pb-button sm" :disabled="busy" @click="bulk('publish')">Publish</button>
        </template>
        <button v-if="status === 'in_review' || status === 'new'" type="button" class="pb-button sm outline" :disabled="busy" @click="bulk('approve')">Approve</button>
        <button v-if="status !== 'archived'" type="button" class="pb-button sm outline" :disabled="busy" @click="bulk('archive')">Archive</button>
        <button v-if="status === 'spam'" type="button" class="pb-button sm outline" :disabled="busy" @click="bulk('not_spam')">Not spam</button>
        <button type="button" class="pb-button sm ghost" @click="selected = []">Clear</button>
      </div>
    </div>

    <SkeletonList v-if="loading" :rows="6" label="Loading your inbox…" />
    <div v-else-if="failed" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i>
      <span>We couldn't load your inbox. Check your connection and try again.</span>
      <button type="button" class="pb-button sm outline" @click="fetch">Retry</button></div>
    <EmptyState v-else-if="!items.length && !totalAll" icon="mdi-inbox-outline" title="No feedback yet"
                text="Copy your collection link and ask a customer for feedback.">
      <button v-if="formLink" type="button" class="pb-button" @click="copyLink">Copy link</button>
      <router-link to="/testimonials/import" class="pb-button outline">Import existing testimonials</router-link>
    </EmptyState>
    <EmptyState v-else-if="!items.length && (activeFilters || q)" icon="mdi-filter-remove-outline"
                :title="elsewhere.length ? 'Nothing matches in ' + tabLabel : 'Nothing matches these filters'"
                :text="elsewhere.length ? 'Found in ' + elsewhereText + '.' : ''">
      <button v-for="tab in elsewhere" :key="tab.key" type="button" class="pb-button" @click="setStatus(tab.key)">Show {{ tab.label }} ({{ tab.count }})</button>
      <button type="button" class="pb-button outline" @click="clearFilters">Clear filters</button>
    </EmptyState>
    <div v-else-if="!items.length" class="pb-card flat ib-none"><p class="pb-body">{{ emptyTab }}</p></div>

    <ul v-else class="ib-list" :aria-label="tabLabel + ' feedback'">
      <li v-for="row in items" :key="row.id" :class="['ib-row', { open: openId === row.id, unread: row.status === 'new' }]">
        <label class="ib-select"><span class="pb-sr-only">Select feedback from {{ row.name || 'Anonymous' }}</span>
          <input v-model="selected" type="checkbox" :value="row.id"></label>
        <button type="button" class="ib-open" :aria-current="openId === row.id ? 'true' : null" @click="open(row.id)">
          <span class="ib-who">
            <span class="ib-name">{{ row.name || 'Anonymous' }}<span v-if="row.company" class="ib-company"> · {{ row.company }}</span></span>
            <span class="ib-when">{{ when(row.created_at) }}</span>
          </span>
          <span class="ib-text">{{ row.excerpt }}</span>
          <span class="ib-meta">
            <span v-if="row.rating" class="ib-stars" :aria-label="'Rated ' + row.rating + ' out of 5'" role="img">
              <i class="mdi mdi-star" aria-hidden="true"></i>{{ row.rating }}</span>
            <span v-if="row.form_name" class="ib-form">{{ row.form_name }}</span>
            <span v-else-if="row.source_label" class="ib-form">{{ row.source_label }}</span>
            <span v-if="brandFilter === 'all'" class="ib-form">{{ row.brand_name }}</span>
            <!-- Each row's status, as in the drawer; a private row's permission chip already says "Private". -->
            <StatusChip v-if="row.status !== 'private'" :status="row.status" />
            <StatusChip :consent="row.may_publish" />
            <span v-if="row.public && !row.public.showing" class="pb-chip is-warning" :title="row.public.reason">
              <i class="mdi mdi-eye-off-outline pb-ico" aria-hidden="true"></i>Not showing publicly</span>
          </span>
        </button>
      </li>
    </ul>
    <div v-if="pages > 1" class="pb-actions ib-pages">
      <button type="button" class="pb-button sm outline" :disabled="page <= 1" @click="goPage(page - 1)">Previous</button>
      <span class="pb-muted">Page {{ page }} of {{ pages }}</span>
      <button type="button" class="pb-button sm outline" :disabled="page >= pages" @click="goPage(page + 1)">Next</button>
    </div>

    <transition name="ib-drawer">
      <div v-if="openId" class="ib-overlay" @click.self="close" @keydown.esc="close">
        <aside class="ib-drawer" role="dialog" aria-modal="true" aria-label="Feedback details">
          <SubmissionDetail :id="openId" @close="close" @changed="updateRow" />
        </aside>
      </div>
    </transition>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue';
import PageHeader from '../components/PageHeader.vue';
import SkeletonList from '../components/SkeletonList.vue';
import StatusChip, { STATUS_LABELS } from '../components/StatusChip.vue';
import SubmissionDetail from '../components/SubmissionDetail.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import { copyText } from '../components/SnippetBox.vue';
import LockedAction from '../components/LockedAction.vue';
import workspace from '../mixins/workspace';
import { get, post, message } from '../api';
import { formatRelative, listText, plural } from '../lib/format';
import { publishedToast } from '../lib/showing';
import { toast } from '../lib/toast';
import { collectUrl } from '../publicUrls';

const ORDER = ['new', 'in_review', 'awaiting_client', 'approved', 'published', 'private', 'archived', 'spam'];
// "All" is every status but spam, as in a mail client.
const TAB_KEYS = ['all'].concat(ORDER);
const EMPTY = {
  all: 'Everything so far is in Spam.',
  new: 'No new feedback. New responses land here first.', in_review: 'Nothing in review.',
  awaiting_client: 'Nothing is waiting for a client.', approved: 'Nothing approved and waiting to be published.',
  published: 'Nothing published yet.', private: 'No private feedback.', archived: 'Nothing archived.', spam: 'No spam. Nice.'
};

export default {
  name: 'InboxView',
  components: { EmptyState, LockedAction, PageHeader, SkeletonList, StatusChip, SubmissionDetail, UpgradeDialog },
  mixins: [workspace],
  data() {
    const query = this.$route.query;
    return {
      status: TAB_KEYS.indexOf(query.status) >= 0 ? query.status : 'new', q: query.q || '', formId: query.form || '', rating: query.rating || '',
      from: query.from || '', to: query.to || '', tag: query.tag || '', brandFilter: query.brand === 'all' ? 'all' : 'current',
      page: 1, pages: 1, items: [], counts: {}, totals: {}, countsKnown: false, loading: true, failed: false, forms: [], selected: [], busy: false,
      showFilters: false, upgrade: null, upgradeOpen: false, lastFocus: null, request: 0
    };
  },
  computed: {
    // At the plan's cap, with a plan above that publishes more: Publish shows the cap (lib/plans.js) instead of failing.
    publishCap() { return this.capOf('published', this.usage.published || 0); },
    openId() { return this.$route.params.id ? Number(this.$route.params.id) : null; },
    tabs() {
      const keys = ORDER.filter(key => key !== 'awaiting_client' || this.limits.client_approvals || this.totals.awaiting_client);
      return [{ key: 'all', label: 'All' }].concat(keys.map(key => ({ key, label: STATUS_LABELS[key] })));
    },
    tabLabel() { return STATUS_LABELS[this.status] || 'All'; },
    // The brand's feedback whatever the search and filters: "No feedback yet" only when there is none at all.
    totalAll() { return ORDER.reduce((sum, key) => sum + (this.totals[key] || 0), 0); },
    // While searching or filtering, the other tabs that hold matches, offered from the empty state.
    elsewhere() {
      return this.tabs.filter(tab => tab.key !== 'all' && tab.key !== this.status && this.counts[tab.key])
        .map(tab => ({ key: tab.key, label: tab.label, count: this.counts[tab.key] }));
    },
    elsewhereText() { return listText(this.elsewhere.map(tab => tab.label + ' (' + tab.count + ')')); },
    appliedQ() { return String(this.$route.query.q || '').trim(); },
    matching() { return !!(this.appliedQ || this.activeFilters); },
    matchingText() {
      const found = this.tabCount('all') + (this.counts.spam || 0);
      return plural(found, 'match', 'matches') + (this.appliedQ ? ' for “' + this.appliedQ + '”' : '') + ' across your inbox. '
        + 'The numbers on the tabs count matches.';
    },
    // "All brands" is a scope chosen in the brand switcher, not a filter.
    activeFilters() { return [this.brandFilter === 'all' ? '' : this.formId, this.rating, this.from, this.to, this.tag].filter(Boolean).length; },
    formLink() { const form = this.forms.find(item => item.status === 'active'); return form ? collectUrl(form) : ''; },
    emptyTab() { return EMPTY[this.status] || 'Nothing here.'; },
    subtitle() {
      if (!this.totalAll) return 'Every response lands here with the customer\'s permission choice.';
      return (this.brandFilter === 'all' ? 'All brands · ' : '') + plural(this.totals.new || 0, 'new response') + ' · '
        + plural(this.totals.approved || 0, 'approved testimonial') + ' ready to publish';
    }
  },
  watch: {
    brandId() { this.selected = []; this.loadForms(); this.fetch(); },
    // "All brands" or one brand, from the brand switcher (or a link such as the sidebar's "Inbox (N new)").
    '$route.query.brand'(value) {
      const scope = value === 'all' ? 'all' : 'current';
      if (scope === this.brandFilter) return;
      this.brandFilter = scope;
      this.page = 1;
      this.selected = [];
      this.fetch();
    },
    '$route.query.status'(value) { if (value && value !== this.status && TAB_KEYS.indexOf(value) >= 0) { this.status = value; this.fetch(); } }
  },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.showFilters = this.activeFilters > 0;
    this.loadForms();
    this.fetch();
    document.addEventListener('keydown', this.onKey);
  },
  beforeDestroy() { document.removeEventListener('keydown', this.onKey); },
  methods: {
    when: formatRelative,
    params() {
      const params = { status: this.status, page: this.page };
      if (this.q.trim()) params.q = this.q.trim();
      if (this.formId && this.brandFilter !== 'all') params.form_id = this.formId;
      if (this.rating) params.rating = this.rating;
      if (this.from) params.from = this.from;
      if (this.to) params.to = this.to;
      if (this.tag.trim()) params.tag = this.tag.trim();
      return params;
    },
    async fetch() {
      if (!this.brandId) return;
      // Several changes can start loads at once (a brand and a tab from one link): only the newest one lands.
      const request = ++this.request;
      this.loading = true;
      this.failed = false;
      try {
        const ref = this.brandFilter === 'all' ? 'all' : this.brandId;
        const data = await get('/brands/' + ref + '/submissions', this.params());
        if (request !== this.request) return;
        this.items = data.items;
        this.counts = data.counts;
        this.totals = data.totals || data.counts;
        this.countsKnown = true;
        this.pages = data.pages;
      } catch (error) {
        if (request === this.request) this.failed = true;
      } finally {
        if (request === this.request) this.loading = false;
      }
    },
    async loadForms() {
      if (!this.brandId) return;
      try { this.forms = (await get('/brands/' + this.brandId + '/forms')).forms || []; } catch (error) { this.forms = []; }
    },
    syncQuery() {
      const query = { status: this.status };
      ['q', 'from', 'to', 'tag', 'rating'].forEach(key => { if (this[key]) query[key] = String(this[key]); });
      if (this.formId) query.form = String(this.formId);
      if (this.brandFilter === 'all') query.brand = 'all';
      const path = this.openId ? '/inbox/' + this.openId : '/inbox';
      this.$router.replace({ path, query }).catch(() => {});
    },
    setStatus(key) { this.status = key; this.page = 1; this.selected = []; this.syncQuery(); this.fetch(); },
    // Matches per tab while searching or filtering (the tabs' numbers); "All" sums every tab but Spam.
    tabCount(key) {
      if (key !== 'all') return this.counts[key] || 0;
      return ORDER.reduce((sum, status) => sum + (status === 'spam' ? 0 : (this.counts[status] || 0)), 0);
    },
    search() { this.page = 1; this.syncQuery(); this.fetch(); },
    // A new text search looks in every tab but Spam, so feedback turns up whatever its status (FR-020, FR-023); the
    // tabs then show where the matches are.
    submitSearch() {
      const text = this.q.trim();
      if (text && text !== (this.$route.query.q || '') && this.status !== 'spam' && this.status !== 'all') {
        this.status = 'all';
        this.selected = [];
      }
      this.search();
    },
    goPage(page) { this.page = page; this.fetch(); window.scrollTo(0, 0); },
    clearFilters() {
      this.q = ''; this.formId = ''; this.rating = ''; this.from = ''; this.to = ''; this.tag = '';
      this.search();
    },
    open(id) {
      this.lastFocus = document.activeElement;
      this.$router.push({ path: '/inbox/' + id, query: this.$route.query }).catch(() => {});
    },
    close() {
      this.$router.push({ path: '/inbox', query: this.$route.query }).catch(() => {});
      this.fetch();
      this.$nextTick(() => { if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus(); });
    },
    onKey(event) { if (event.key === 'Escape' && this.openId) this.close(); },
    // An action in the drawer: the row changes in place (it stays in the list until the drawer closes), and the tab
    // numbers, the header line and the sidebar's "Inbox (N new)" follow at once.
    updateRow(detail) {
      const row = this.items.find(item => item.id === detail.id);
      const moved = !row || row.status !== detail.status || row.may_publish !== detail.may_publish;
      if (row) { row.status = detail.status; row.may_publish = detail.may_publish; row.excerpt = (detail.quote_text || '').split('\n')[0]; }
      if (moved) {
        this.refreshCounts();
        this.$store.dispatch('praiseboard/load').catch(() => {});
      }
    },
    async refreshCounts() {
      if (!this.brandId) return;
      try {
        const data = await get('/brands/' + (this.brandFilter === 'all' ? 'all' : this.brandId) + '/submissions', this.params());
        this.counts = data.counts;
        this.totals = data.totals || data.counts;
      } catch (error) { /* the next full load corrects them */ }
    },
    async bulk(action) {
      this.busy = true;
      try {
        const result = await post('/submissions/actions', { ids: this.selected, action });
        const done = result.done.length;
        if (result.limit) { this.upgrade = result.limit; this.upgradeOpen = true; }
        if (done && action === 'publish') {
          const ids = this.items.filter(row => result.done.indexOf(row.id) >= 0).map(row => row.brand_id);
          const brands = this.$store.getters['praiseboard/brands'].filter(brand => ids.indexOf(brand.id) >= 0);
          const outcome = publishedToast(brands, done);
          toast(outcome.text, 'success', outcome.action);
        } else if (done) toast(plural(done, 'item') + ' updated.');
        else if (result.failed.length && !result.limit) toast(result.failed[0].message, 'error');
        this.selected = [];
        this.$store.dispatch('praiseboard/load').catch(() => {});
        this.fetch();
      } catch (error) {
        toast(message(error), 'error');
      } finally {
        this.busy = false;
      }
    },
    async copyLink() { await copyText(this.formLink); toast('Link copied.'); }
  }
};
</script>

<style scoped>
.ib-tabs{display:flex;gap:4px;overflow-x:auto;border-bottom:1px solid #E6DED2;margin-bottom:16px;scrollbar-width:thin}
.ib-tab{display:inline-flex;align-items:center;gap:8px;min-height:44px;padding:8px 14px;border:0;border-bottom:2px solid transparent;
  background:none;font:600 14px var(--font-body);color:#4F4A45;cursor:pointer;white-space:nowrap;margin-bottom:-1px}
.ib-tab:hover{color:#1F2328}
.ib-tab.active{color:#1F2328;border-bottom-color:#B4472B}
.ib-count{display:inline-grid;place-items:center;min-width:24px;height:22px;padding:0 7px;border-radius:999px;background:#F3EEE6;color:#4F4A45;font-size:12px;font-variant-numeric:tabular-nums}
.ib-tab.active .ib-count{background:rgba(180,71,43,.12);color:#7C2F1B}
.ib-filters{display:flex;gap:10px;align-items:center;margin-bottom:12px}
.ib-search{position:relative;flex:1;min-width:0}
.ib-search .pb-input{padding-left:40px}
.ib-search-icon{position:absolute;left:13px;top:50%;transform:translateY(-50%);font-size:19px;color:#6B635B;pointer-events:none}
.ib-badge{display:inline-grid;place-items:center;min-width:20px;height:20px;border-radius:999px;background:#B4472B;color:#fff;font-size:11.5px}
.ib-panel{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(180px,100%),1fr));gap:12px 16px;align-items:end;margin-bottom:16px;padding:18px}
.ib-matching{display:flex;align-items:center;gap:6px;margin:0 0 12px;font-size:14px}
.ib-f .pb-label{margin-bottom:6px}
.ib-clear{display:flex;align-items:flex-end}
.ib-bulk{position:sticky;top:72px;z-index:4;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:10px;
  padding:10px 14px;margin-bottom:12px;border-radius:12px;background:#1F2328;color:#fff}
.ib-bulk span,.ib-bulk strong{color:#fff}
.ib-bulk .pb-button.outline{background:transparent;color:#fff!important;border-color:rgba(255,255,255,.4)}
.ib-bulk .pb-button.ghost{color:#fff!important}
.ib-bulk .pb-button.ghost:hover{background:rgba(255,255,255,.12)}
.ib-list{list-style:none;margin:0;padding:0;display:grid;gap:8px}
.ib-row{display:flex;align-items:stretch;border:1px solid #E6DED2;border-radius:14px;background:#fff;transition:border-color .15s ease,box-shadow .15s ease}
.ib-row:hover{border-color:#D4C9B8;box-shadow:var(--pb-shadow-card)}
.ib-row.open{border-color:#B4472B}
.ib-row.unread{border-left:4px solid #B4472B}
.ib-select{display:flex;align-items:flex-start;padding:18px 0 0 16px;margin:0!important;cursor:pointer}
.ib-select input{width:18px;height:18px;accent-color:#B4472B}
.ib-open{flex:1;min-width:0;display:flex;flex-direction:column;gap:6px;padding:14px 16px;border:0;background:none;text-align:left;cursor:pointer;border-radius:14px}
.ib-who{display:flex;justify-content:space-between;gap:12px}
.ib-name{font:700 14.5px/1.35 var(--font-body);color:#1F2328;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ib-company{font-weight:500;color:#4F4A45}
.ib-when{flex:0 0 auto;font-size:13px;color:#6B635B}
.ib-text{font:400 15px/1.5 var(--font-body);color:#4F4A45;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.ib-meta{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:2px}
.ib-stars{display:inline-flex;align-items:center;gap:3px;font:700 13px var(--font-body);color:#7A4605}
.ib-stars .mdi{color:#B7791F}
.ib-form{font-size:13px;color:#6B635B}
.ib-none{text-align:center;padding:36px}
.ib-pages{justify-content:center;margin-top:18px}
.ib-overlay{position:fixed;top:48px;right:0;bottom:0;left:0;z-index:200;background:rgba(31,35,40,.32);display:flex;justify-content:flex-end}
.ib-drawer{width:min(620px,100vw);height:100%;overflow-y:auto;background:#fff;box-shadow:-12px 0 40px rgba(31,35,40,.18);overscroll-behavior:contain}
.ib-drawer-enter-active,.ib-drawer-leave-active{transition:opacity .2s ease}
.ib-drawer-enter-active .ib-drawer,.ib-drawer-leave-active .ib-drawer{transition:transform .22s ease}
.ib-drawer-enter,.ib-drawer-leave-to{opacity:0}
.ib-drawer-enter .ib-drawer,.ib-drawer-leave-to .ib-drawer{transform:translateX(24px)}
@media(prefers-reduced-motion:reduce){.ib-drawer-enter-active,.ib-drawer-leave-active,.ib-drawer-enter-active .ib-drawer,.ib-drawer-leave-active .ib-drawer{transition:none}}
@media(max-width:600px){
  .ib-filters{flex-wrap:wrap}
  .ib-search{flex:1 1 100%}
  .ib-filters .pb-button{flex:1}
  .ib-bulk{top:60px}
  .ib-select{padding:16px 0 0 12px}
  .ib-open{padding:12px}
}
</style>
